import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, tap, catchError, of } from "rxjs";
import { map } from "rxjs/operators";
import { API_BASE_URL } from "../../core/base.service";

/** 后端 API 统一响应包装 */
interface ApiResponse<T> {
  statusCode: number;
  items: T;
  msg: string;
}

/** 一键喊话权限状态（后端权威校验结果） */
export interface BroadcastEntitlement {
  enabled: boolean;
}

/** 绑定设备项（教师视角：教室名按绑定各自记录） */
export interface BroadcastDeviceItem {
  id: number;
  deviceId: string | null;
  classroomName: string | null;
  lastHeartbeatAt: string | null;
}

/** 设备绑定码生成结果 */
export interface BindCodeResult {
  code: string;
  expiresIn: number;
}

/** 设备激活结果 */
export interface DeviceActivateResult {
  classroomName: string;
  deviceToken: string;
}

/** 发送结果 */
export interface BroadcastSendResult {
  broadcastId: number;
}

/** 记录中的逐台投递状态 */
export interface BroadcastRecordDeviceState {
  deviceId: number;
  classroomName: string;
  status: string;
  finishedAt: string | null;
}

/** 喊话记录 */
export interface BroadcastRecord {
  id: number;
  content: string;
  teacherName: string | null;
  createdAt: string;
  devices: BroadcastRecordDeviceState[];
}

@Injectable({ providedIn: "root" })
export class BroadcastService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL, { optional: true }) || "";

  /** 当前喊话权限（内存缓存，不持久化；以服务端校验为准） */
  private readonly _entitlement = signal<BroadcastEntitlement | null>(null);
  readonly entitlement = this._entitlement.asReadonly();

  /** 查询喊话权限（每请求权威校验；进入喊话页/绑定/发送前调用） */
  checkEntitlement(): Observable<BroadcastEntitlement> {
    return this.http
      .get<ApiResponse<BroadcastEntitlement>>(
        `${this.apiBaseUrl}/broadcast/entitlement`,
      )
      .pipe(
        map((res) => {
          // 后端非泛型 ResultModel 返回 items 为单个对象
          const item = res?.items;
          if (!item) throw new Error("Invalid entitlement response");
          return item;
        }),
        tap((entitlement) => this._entitlement.set(entitlement)),
      );
  }

  /** 刷新喊话权限（静默，不抛异常） */
  refreshEntitlement(): Observable<BroadcastEntitlement | null> {
    return this.checkEntitlement().pipe(catchError(() => of(null)));
  }

  /** 兑换喊话码（开通一键喊话） */
  redeemCode(code: string): Observable<BroadcastEntitlement> {
    const formData = new FormData();
    formData.append("code", code.trim().toUpperCase());

    return this.http
      .post<ApiResponse<BroadcastEntitlement>>(
        `${this.apiBaseUrl}/broadcast/redeem`,
        formData,
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200) {
            throw new Error(res.msg || "兑换失败");
          }
          const item = res.items;
          if (!item || item.enabled !== true) {
            throw new Error(res.msg || "兑换失败");
          }
          return item;
        }),
        tap((entitlement) => this._entitlement.set(entitlement)),
      );
  }

  /** 生成设备绑定码（6 位数字、10 分钟有效；需已开通喊话权限） */
  createBindCode(classroomName: string): Observable<BindCodeResult> {
    const formData = new FormData();
    formData.append("classroomName", classroomName.trim());

    return this.http
      .post<ApiResponse<BindCodeResult>>(
        `${this.apiBaseUrl}/broadcast/bind-code`,
        formData,
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200) {
            throw new Error(res.msg || "生成失败");
          }
          const item = res.items;
          if (!item?.code) {
            throw new Error(res.msg || "生成失败");
          }
          return item;
        }),
      );
  }

  /** 我的绑定设备列表 */
  myDevices(): Observable<BroadcastDeviceItem[]> {
    return this.http
      .get<ApiResponse<{ devices?: BroadcastDeviceItem[] }>>(
        `${this.apiBaseUrl}/broadcast/device/my`,
      )
      .pipe(map((res) => res?.items?.devices ?? []));
  }

  /** 移除某台设备的绑定（仅自己名下） */
  removeDevice(id: number): Observable<boolean> {
    return this.http
      .delete<ApiResponse<{ removed?: boolean }>>(
        `${this.apiBaseUrl}/broadcast/device/my/${id}`,
      )
      .pipe(map((res) => res?.statusCode === 200));
  }

  /** 设备激活（免登录，绑定码即凭证；deviceId 为壳本地持久化的 uuid） */
  deviceActivate(deviceId: string, code: string): Observable<DeviceActivateResult> {
    const formData = new FormData();
    formData.append("deviceId", deviceId.trim());
    formData.append("code", code.trim());

    return this.http
      .post<ApiResponse<DeviceActivateResult>>(
        `${this.apiBaseUrl}/broadcast/device/activate`,
        formData,
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200) {
            throw new Error(res.msg || "激活失败");
          }
          const item = res.items;
          if (!item?.deviceToken) {
            throw new Error(res.msg || "激活失败");
          }
          return item;
        }),
      );
  }

  /** 设备清除全部绑定（凭 deviceToken 鉴权） */
  deviceClear(deviceId: string, deviceToken: string): Observable<number> {
    const formData = new FormData();
    formData.append("deviceId", deviceId.trim());
    formData.append("deviceToken", deviceToken.trim());

    return this.http
      .post<ApiResponse<{ removed?: number }>>(
        `${this.apiBaseUrl}/broadcast/device/clear`,
        formData,
      )
      .pipe(map((res) => res?.items?.removed ?? 0));
  }

  /** 发送喊话（JSON body；403 未开通 / 429 限流 / 400 校验 → HTTP 错误，msg 中文） */
  sendBroadcast(
    deviceIds: number[],
    content: string,
  ): Observable<BroadcastSendResult> {
    return this.http
      .post<ApiResponse<BroadcastSendResult>>(
        `${this.apiBaseUrl}/broadcast/send`,
        { deviceIds, content },
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200 || !res.items?.broadcastId) {
            throw new Error(res.msg || "发送失败");
          }
          return res.items;
        }),
      );
  }

  /** 我的喊话记录 */
  myRecords(): Observable<BroadcastRecord[]> {
    return this.http
      .get<ApiResponse<{ records?: BroadcastRecord[] }>>(
        `${this.apiBaseUrl}/broadcast/record`,
      )
      .pipe(map((res) => res?.items?.records ?? []));
  }

  /** 重发某条历史（同约束；生成新记录） */
  resendBroadcast(id: number): Observable<BroadcastSendResult> {
    return this.http
      .post<ApiResponse<BroadcastSendResult>>(
        `${this.apiBaseUrl}/broadcast/resend/${id}`,
        {},
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200 || !res.items?.broadcastId) {
            throw new Error(res.msg || "重发失败");
          }
          return res.items;
        }),
      );
  }

  /** 清除缓存（登出时调用） */
  clearCache(): void {
    this._entitlement.set(null);
  }
}
